import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { CHART_CONFIG, REPORT_COLORS } from '../../constants';
import type { MonthlyMix } from '../../../types/report';
import './EnergyMixChart.css';

const SERIES = [
  { key: 'pctImported', name: 'Imported from New Brunswick', color: REPORT_COLORS.imported },
  { key: 'pctRenewable', name: 'On-island renewables', color: REPORT_COLORS.renewable },
  { key: 'pctFossil', name: 'On-island fossil fuel', color: REPORT_COLORS.fossil },
] as const;

/** "2026-01" → "Jan '26". The apostrophe stops "Dec 25" reading as a date. */
function monthLabel(month: string): string {
  const [year, m] = month.split('-');
  const date = new Date(Number(year), Number(m) - 1, 1);
  return `${date.toLocaleString('en-CA', { month: 'short' })} '${year.slice(2)}`;
}

export function EnergyMixChart({ monthly }: { monthly: MonthlyMix[] }) {
  const data = monthly.map((m) => ({
    ...m,
    label: monthLabel(m.month),
  }));

  return (
    <figure className="EnergyMixChart">
      <figcaption className="EnergyMixChart-caption">
        Share of on-island electricity demand met by each source, by month.
      </figcaption>
      <div className="EnergyMixChart-plot">
        <ResponsiveContainer width="100%" height={320}>
          <BarChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 4 }}>
            <CartesianGrid stroke={CHART_CONFIG.gridStroke} vertical={false} />
            <XAxis dataKey="label" stroke={CHART_CONFIG.axisStroke} tickLine={false} fontSize={12} />
            <YAxis
              stroke={CHART_CONFIG.axisStroke}
              tickLine={false}
              axisLine={false}
              fontSize={12}
              unit="%"
              /* The three shares sum to 100 but floating-point addition lands a
                 hair above it, which otherwise pushes the axis to a stray
                 100.1% tick. Fixed ticks and a clamped domain keep it honest. */
              domain={[0, 100]}
              ticks={[0, 25, 50, 75, 100]}
              allowDataOverflow
            />
            <Tooltip
              contentStyle={CHART_CONFIG.tooltipStyle}
              cursor={{ fill: 'rgba(255,255,255,0.04)' }}
              formatter={(value: number, name: string) => [`${value}%`, name]}
            />
            <Legend wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
            {SERIES.map((s) => (
              <Bar
                key={s.key}
                dataKey={s.key}
                name={s.name}
                stackId="mix"
                fill={s.color}
                /* 2px surface gap keeps adjacent stacked segments from fusing. */
                stroke={CHART_CONFIG.tooltipStyle.background}
                strokeWidth={2}
                isAnimationActive={false}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>

      <details className="EnergyMixChart-table">
        <summary>View as table</summary>
        <table>
          <thead>
            <tr>
              <th scope="col">Month</th>
              <th scope="col">Avg load</th>
              <th scope="col">Imported</th>
              <th scope="col">Renewable</th>
              <th scope="col">Fossil</th>
            </tr>
          </thead>
          <tbody>
            {data.map((m) => (
              <tr key={m.month}>
                <td>{m.label}</td>
                <td>{m.avgLoadMw} MW</td>
                <td>{m.pctImported}%</td>
                <td>{m.pctRenewable}%</td>
                <td>{m.pctFossil}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </details>
    </figure>
  );
}
